import React, {
  useEffect,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  modelReducer,
  initialModelState,
  getRADs as getRADsFromModel, // Import the selector
  updateRADs as updateRADsAction,
} from "./ModelLogic.jsx";
import { useModelHistory } from "./useModelHistory.jsx";
import { HistoryContext } from "@cadenza/history-react";
import { useLocalStorage } from "@cadenza/io-hooks";
import { RADStoreContext } from "@cadenza/rad-react";

// Context
const ModelContext = createContext();

export const useModel = () => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error("useModel must be used within a ModelProvider");
  }
  return context;
};

const ModelProvider = ({ children }) => {
  const [isChanged, setIsChanged] = useState(false);

  // Persist model in localStorage using hook
  const [storedModel, setStoredModel] = useLocalStorage(
    "model",
    initialModelState
  );

  const {
    state: presentState,
    reset,
    update,
    undo,
    redo,
    canUndo,
    canRedo,
    past,
    future,
    undoAll,
    redoAll,
  } = useModelHistory(storedModel);

  // Set isChanged to false after initial load
  useEffect(() => {
    setIsChanged(false);
  }, []);

  const clearHistory = useCallback(() => {
    reset(presentState);
  }, [reset, presentState]);

  const resetModel = useCallback(
    (data, sideEffectCallback) => {
      return new Promise((resolve, reject) => {
        try {
          const modelData = data ?? { ...initialModelState };
          const payload = { ...modelData };
          const type = "CHANGE_MODEL";
          const newModel = modelReducer(presentState, { type, payload });

          reset(newModel); // Use the reset function from useModelHistory
          if (sideEffectCallback) {
            sideEffectCallback();
          }
          setIsChanged(false);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    [reset, presentState]
  );

  const saveModel = useCallback(() => {
    // Saving a model doesn;t actually change state other then the change flag
    setIsChanged(false);
  }, []);

  const updateModel = useCallback(
    (type, payload, sideEffectCallback, source = "user") => {
      return new Promise((resolve, reject) => {
        try {
          // Calculate the new state
          const newState = modelReducer(presentState, { type, payload });

          // Pass the new state through the history-aware update
          update(newState, source);

          if (sideEffectCallback) {
            sideEffectCallback();
          }

          setIsChanged(true); // Mark model as changed
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    [presentState, update]
  );

  // sync presentState -> local storage
  useEffect(() => {
    setStoredModel(presentState);
  }, [presentState, setStoredModel]);

  // --- Define RAD context functions directly here ---
  const getRADsForContext = useCallback(() => {
    // Use the selector directly with the current state from useModelHistory
    return getRADsFromModel(presentState);
  }, [presentState]); // Depends on the current state

  const updateRADsForContext = useCallback(
    (rads) => {
      // Create the action using the action creator from ModelLogic
      const action = updateRADsAction(rads);
      // Dispatch the action using the ModelProvider's updateModel function
      updateModel(action.type, action.payload);
    },
    [updateModel]
  ); // Depends on the stable updateModel function

  const modelContext = useMemo(
    () => ({
      model: presentState || {},
      isChanged,
      resetModel,
      saveModel,
      updateModel,
      undo,
      redo,
      canUndo,
      canRedo,
      undoCount: past.length, // Number of undoable states
      redoCount: future.length,
      undoAll,
      redoAll,
      clearHistory,
    }),
    [
      presentState,
      isChanged,
      resetModel,
      saveModel,
      updateModel,
      undo,
      redo,
      canUndo,
      canRedo,
      past.length,
      future.length,
      undoAll,
      redoAll,
      clearHistory,
    ]
  );

  const historyContextValue = useMemo(
    () => ({
      state: presentState,
      reset,
      update,
      undo,
      redo,
      canUndo,
      canRedo,
      past,
      future,
      undoAll,
      redoAll,
      undoCount: past.length,
      redoCount: future.length,
      clearHistory,
    }),
    [
      presentState,
      reset,
      update,
      undo,
      redo,
      canUndo,
      canRedo,
      past,
      future,
      undoAll,
      redoAll,
      clearHistory,
    ]
  );

  // Use the RADStoreContext to get the RADs
  const radStoreContext = useMemo(
    () => ({
      getRADs: getRADsForContext,
      updateRADs: updateRADsForContext,
    }),
    [getRADsForContext, updateRADsForContext]
  );

  return (
    <ModelContext.Provider value={modelContext}>
      <HistoryContext.Provider value={historyContextValue}>
        <RADStoreContext.Provider value={radStoreContext}>
          {children}
        </RADStoreContext.Provider>
      </HistoryContext.Provider>
    </ModelContext.Provider>
  );
};

export { ModelContext, ModelProvider };
